import React from "react";
import { useNavigate } from "react-router-dom";
import styles from "./DashboardBaseComponent.module.css";

// Optional override via env; falls back to "/app"
const APP_BASE = process.env.REACT_APP_APP_BASE_PATH || "/app";

export default function DashboardBaseComponent() {
  const navigate = useNavigate();

  const goTo = (path) => () => navigate(path, { replace: false });

  const onUploadCsv = goTo(`${APP_BASE}/upload`);
  const onAddTransaction = goTo(`${APP_BASE}/transactions/new`);
  const onCreateTag = goTo(`${APP_BASE}/tags`);

  return (
    <div className="p-3 p-lg-4">
      <div className="row g-3">
        {/* ===== Left / Main ===== */}
        <div className="col-12 col-lg-8">
          {/* Welcome / Intro */}
          <div className={`card ${styles.surfaceCard}`}>
            <div className="card-body">
              <div className="d-flex align-items-center gap-2 mb-1">
                <h2 className="h5 fw-bold mb-0">Welcome to your dashboard</h2>
                <span className={styles.envChip} title="Preview state">PREVIEW</span>
              </div>
              <p className="text-secondary mb-0">
                This space will fill with Insights, Budgets, and Forecasts as you connect accounts and upload CSVs.
              </p>
            </div>
          </div>

          {/* Insights (Spending Trends) */}
          <div className={`card ${styles.surfaceCard}`}>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h3 className="h6 fw-bold mb-0">Insights</h3>
                <span className="text-secondary small">Last 30 days</span>
              </div>
              <div className={styles.placeholderChart} aria-label="Insights chart loading placeholder" />
              <p className="text-secondary small mt-2 mb-0">
                Connect an account to unlock personalized insights.
              </p>
            </div>
          </div>

          {/* Budgets Overview */}
          <div className={`card ${styles.surfaceCard}`}>
            <div className="card-body">
              <h3 className="h6 fw-bold mb-3">Budgets</h3>

              <div className="mb-3">
                <div className={styles.budgetRow}>
                  <div className={styles.budgetLabel}>Groceries</div>
                  <div className={styles.budgetPct}>—</div>
                </div>
                <div className={styles.placeholderBar} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={0} />
              </div>

              <div className="mb-3">
                <div className={styles.budgetRow}>
                  <div className={styles.budgetLabel}>Rent</div>
                  <div className={styles.budgetPct}>—</div>
                </div>
                <div className={styles.placeholderBar} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={0} />
              </div>

              <div className="mb-1">
                <div className={styles.budgetRow}>
                  <div className={styles.budgetLabel}>Entertainment</div>
                  <div className={styles.budgetPct}>—</div>
                </div>
                <div className={styles.placeholderBar} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={0} />
              </div>

              <p className="text-secondary small mt-2 mb-0">
                Create a budget to see spending progress.
              </p>
            </div>
          </div>

          {/* Forecast / Cashflow */}
          <div className={`card ${styles.surfaceCard}`}>
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h3 className="h6 fw-bold mb-0">Forecast</h3>
                <span className="text-secondary small">Next 30 days</span>
              </div>
              <div className={styles.placeholderLineChart} aria-label="Forecast chart loading placeholder">
                <div className={styles.line} />
              </div>
              <p className="text-secondary small mt-2 mb-0">
                Forecasts will show your projected balances based on past activity.
              </p>
            </div>
          </div>
        </div>

        {/* ===== Right / Sidebar ===== */}
        <div className="col-12 col-lg-4">
          {/* Quick Actions */}
          <div className={`card ${styles.surfaceCard}`}>
            <div className="card-body">
              <h3 className="h6 fw-bold mb-2">Quick actions</h3>
              <div className="d-grid gap-2">
                <button
                  type="button"
                  className={`btn btn-primary btn-compact ${styles.actionBtn}`}
                  data-action="upload-csv"
                  onClick={onUploadCsv}
                  aria-label="Upload a CSV"
                >
                  + Upload CSV
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-primary btn-compact ${styles.actionBtn}`}
                  data-action="add-transaction"
                  onClick={onAddTransaction}
                  aria-label="Add a transaction"
                >
                  + Add Transaction
                </button>
                <button
                  type="button"
                  className={`btn btn-outline-primary btn-compact ${styles.actionBtn}`}
                  data-action="create-tag"
                  onClick={onCreateTag}
                  aria-label="Create a tag"
                >
                  + Create Tag
                </button>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className={`card ${styles.surfaceCard}`}>
            <div className="card-body">
              <h3 className="h6 fw-bold mb-2">Next steps</h3>
              <ul className={`mb-0 ${styles.compactList}`}>
                <li>Connect an account</li>
                <li>Upload a CSV</li>
                <li>Create your first budget</li>
              </ul>
            </div>
          </div>

          {/* Alerts */}
          <div className={`card ${styles.surfaceCard}`}>
            <div className="card-body">
              <h3 className="h6 fw-bold mb-1">Alerts</h3>
              <p className="text-secondary small mb-0">
                No alerts yet. Once you set up budgets, we’ll notify you about unusual spend or overspending.
              </p>
              <div className={`${styles.placeholderPill} mt-3`} aria-hidden="true" />
              <div className={`${styles.placeholderPill} mt-2`} aria-hidden="true" />
            </div>
          </div>

          {/* Setup Progress / Gamification */}
          <div className={`card ${styles.surfaceCard}`}>
            <div className="card-body">
              <h3 className="h6 fw-bold mb-2">Setup progress</h3>
              <div className={styles.progressWrap} aria-label="Onboarding checklist completion">
                <div className={styles.progressTrack}>
                  <div className={styles.progressFill} style={{ width: "20%" }} />
                </div>
                <div className="d-flex justify-content-between mt-1">
                  <span className="text-secondary small">0/5 completed</span>
                  <span className="text-secondary small">20%</span>
                </div>
              </div>
              <ul className={`mt-3 mb-0 ${styles.compactList}`}>
                <li>✅ Create an account</li>
                <li>⬜ Connect a bank</li>
                <li>⬜ Upload CSV</li>
                <li>⬜ Create tags</li>
                <li>⬜ Set a budget</li>
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
